<template>
  <div class="oss-file-uploader">
    <!-- 上传区域 -->
    <div 
      class="upload-area" 
      :class="{ disabled: disabled || uploading }"
      @click="triggerFileSelect"
      @drop="handleDrop"
      @dragover.prevent
      @dragenter.prevent
    >
      <div v-if="fileList.length === 0" class="upload-placeholder">
        <i class="el-icon-upload"></i>
        <p>{{ placeholder || '点击或拖拽文件到此处上传' }}</p>
        <span>{{ acceptText || '支持的文件类型：' + allowedTypes.join(', ') }}</span>
      </div>
      
      <!-- 文件列表 -->
      <div v-else class="file-list">
        <div 
          v-for="(fileItem, index) in fileList" 
          :key="index" 
          class="file-item"
        >
          <div class="file-info">
            <i class="el-icon-document"></i>
            <div class="file-details">
              <span class="file-name">{{ fileItem.name }}</span>
              <span class="file-size">{{ formatFileSize(fileItem.size) }}</span>
            </div>
            <div class="file-status">
              <span v-if="fileItem.uploading" class="uploading">
                <i class="el-icon-loading"></i> 上传中...
              </span>
              <span v-else-if="fileItem.success" class="success">
                <i class="el-icon-success"></i> 成功
              </span>
              <span v-else-if="fileItem.error" class="error">
                <i class="el-icon-error"></i> 失败
              </span>
            </div>
            <el-button 
              v-if="!fileItem.uploading" 
              type="text" 
              icon="el-icon-delete" 
              class="remove-btn"
              @click.stop="removeFile(index)"
            ></el-button>
          </div>
          
          <!-- 进度条 -->
          <div v-if="fileItem.uploading" class="progress-bar">
            <el-progress 
              :percentage="fileItem.progress" 
              :stroke-width="4"
              :show-text="false"
            ></el-progress>
          </div>
          
          <!-- 错误信息 -->
          <div v-if="fileItem.error" class="error-message">
            {{ fileItem.error }}
          </div>
        </div>
      </div>
    </div>

    <!-- 文件选择输入框 -->
    <input 
      ref="fileInput" 
      type="file" 
      :accept="accept" 
      :multiple="multiple"
      @change="handleFileSelect" 
      style="display: none"
    />

    <!-- 上传按钮 -->
    <div v-if="showUploadButton && fileList.length > 0" class="upload-actions">
      <el-button 
        type="primary" 
        :loading="uploading" 
        :disabled="disabled"
        @click="startUpload"
      >
        {{ uploading ? '上传中...' : '开始上传' }}
      </el-button>
      <el-button @click="clearFiles">清空</el-button>
    </div>

    <!-- 错误提示 -->
    <div v-if="errorMessage" class="error-notice">
      <i class="el-icon-warning"></i>
      <span>{{ errorMessage }}</span>
    </div>
  </div>
</template>

<script>
import ossUploader from '@/utils/ossUpload';

export default {
  name: 'OSSFileUploader',
  props: {
    // 是否支持多文件上传
    multiple: {
      type: Boolean,
      default: false
    },
    // 接受的文件类型
    accept: {
      type: String,
      default: ''
    },
    // 允许的文件类型数组
    allowedTypes: {
      type: Array,
      default: () => []
    },
    // 最大文件大小（字节）
    maxFileSize: {
      type: Number,
      default: 104857600 // 100MB
    },
    // 是否禁用
    disabled: {
      type: Boolean,
      default: false
    },
    // 是否显示上传按钮
    showUploadButton: {
      type: Boolean,
      default: true
    },
    // 是否自动上传
    autoUpload: {
      type: Boolean,
      default: false
    },
    // 自定义上传目录
    uploadDir: {
      type: String,
      default: null
    },
    // 占位符文本
    placeholder: {
      type: String,
      default: ''
    },
    // 接受文件类型文本
    acceptText: {
      type: String,
      default: ''
    },
    // 是否使用原文件名
    useOriginalName: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      fileList: [],
      uploading: false,
      errorMessage: ''
    };
  },
  computed: {
    // 计算接受的文件类型文本
    computedAcceptText() {
      if (this.acceptText) {
        return this.acceptText;
      }
      if (this.allowedTypes.length > 0) {
        return '支持的文件类型：' + this.allowedTypes.join(', ');
      }
      return '';
    }
  },
  methods: {
    /**
     * 触发文件选择
     */
    triggerFileSelect() {
      if (this.disabled || this.uploading) return;
      this.$refs.fileInput.click();
    },

    /**
     * 处理文件选择
     */
    handleFileSelect(event) {
      const files = Array.from(event.target.files);
      this.addFiles(files);
      event.target.value = ''; // 清空input值，允许重复选择相同文件
    },

    /**
     * 处理拖拽上传
     */
    handleDrop(event) {
      if (this.disabled || this.uploading) return;
      
      event.preventDefault();
      const files = Array.from(event.dataTransfer.files);
      this.addFiles(files);
    },

    /**
     * 添加文件到列表
     */
    addFiles(files) {
      files.forEach(file => {
        // 验证文件类型
        if (this.allowedTypes.length > 0 && !ossUploader.validateFileType(file, this.allowedTypes)) {
          this.$message.error(`不支持的文件类型：${file.name}`);
          return;
        }

        // 验证文件大小
        if (!ossUploader.validateFileSize(file, this.maxFileSize)) {
          this.$message.error(`文件大小超出限制：${file.name}`);
          return;
        }

        // 检查是否已存在相同文件
        const exists = this.fileList.find(item => 
          item.name === file.name && item.size === file.size
        );
        
        if (exists) {
          this.$message.warning(`文件已存在：${file.name}`);
          return;
        }

        // 添加到文件列表
        this.fileList.push({
          file: file,
          name: file.name,
          size: file.size,
          uploading: false,
          progress: 0,
          success: false,
          error: null,
          result: null
        });
      });

      // 触发文件列表变化事件
      this.$emit('file-list-change', this.fileList);

      // 如果开启自动上传，立即开始上传
      if (this.autoUpload && this.fileList.length > 0) {
        this.startUpload();
      }
    },

    /**
     * 移除文件
     */
    removeFile(index) {
      this.fileList.splice(index, 1);
      this.$emit('file-list-change', this.fileList);
    },

    /**
     * 清空文件列表
     */
    clearFiles() {
      this.fileList = [];
      this.errorMessage = '';
      this.$emit('file-list-change', this.fileList);
    },

    /**
     * 开始上传
     */
    async startUpload() {
      if (this.uploading || this.fileList.length === 0) return [];

      this.uploading = true;
      this.errorMessage = '';

      try {
        // 获取待上传的文件
        const filesToUpload = this.fileList.filter(item => !item.success && !item.error);
        
        if (filesToUpload.length === 0) {
          this.$message.info('没有需要上传的文件');
          return [];
        }

        // 批量上传文件
        const results = await ossUploader.uploadFiles(
          filesToUpload.map(item => item.file),
          (totalProgress) => {
            // 总体进度回调
            this.$emit('upload-progress', totalProgress);
          },
          (fileProgress, fileIndex) => {
            // 单个文件进度回调
            const actualIndex = this.fileList.findIndex(item => 
              item.file === filesToUpload[fileIndex].file
            );
            if (actualIndex !== -1) {
              this.fileList[actualIndex].progress = fileProgress;
              this.fileList[actualIndex].uploading = true;
            }
          },
          this.uploadDir,
          this.useOriginalName
        );

        // 处理上传结果
        results.forEach((result, index) => {
          const actualIndex = this.fileList.findIndex(item => 
            item.file === filesToUpload[index].file
          );
          
          if (actualIndex !== -1) {
            if (result.success) {
              this.fileList[actualIndex].success = true;
              this.fileList[actualIndex].result = result;
              this.fileList[actualIndex].uploading = false;
              this.fileList[actualIndex].progress = 100;
              
              // 触发单个文件上传成功事件
              this.$emit('file-success', result, actualIndex);
            } else {
              this.fileList[actualIndex].error = result.error;
              this.fileList[actualIndex].uploading = false;
              
              // 触发单个文件上传失败事件
              this.$emit('file-error', result.error, actualIndex);
            }
          }
        });

        // 检查是否所有文件都上传成功
        const successCount = this.fileList.filter(item => item.success).length;
        const totalCount = this.fileList.length;
        
        if (successCount === totalCount) {
          // 移除内部提示，让父组件决定是否显示
          this.$emit('upload-success', this.getSuccessFiles());
        } else {
          // 只在部分成功时显示警告
          this.$message.warning(`${successCount}/${totalCount} 个文件上传成功`);
        }

        // 返回上传结果
        return this.getSuccessFiles();

      } catch (error) {
        console.error('上传失败:', error);
        this.errorMessage = error.message || '上传失败';
        this.$emit('upload-error', error);
        throw error; // 重新抛出错误
      } finally {
        this.uploading = false;
      }
    },

    /**
     * 格式化文件大小
     */
    formatFileSize(bytes) {
      return ossUploader.formatFileSize(bytes);
    },

    /**
     * 获取上传成功的文件
     */
    getSuccessFiles() {
      return this.fileList.filter(item => item.success).map(item => item.result);
    },

    /**
     * 获取所有文件结果
     */
    getAllResults() {
      return this.fileList.map(item => item.result);
    }
  }
};
</script>

<style lang="less" scoped>
.oss-file-uploader {
  .upload-area {
    border: 2px dashed #d1d5db;
    border-radius: 8px;
    padding: 20px;
    cursor: pointer;
    transition: all 0.3s ease;
    min-height: 120px;

    &:hover {
      border-color: #409EFF;
      background-color: #f8fafc;
    }

    &.disabled {
      cursor: not-allowed;
      opacity: 0.6;
      border-color: #e5e7eb;
      background-color: #f9fafb;

      &:hover {
        border-color: #e5e7eb;
        background-color: #f9fafb;
      }
    }

    .upload-placeholder {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 80px;

      i {
        font-size: 24px;
        color: #9ca3af;
        margin-bottom: 8px;
      }

      p {
        margin: 0 0 4px 0;
        font-size: 14px;
        color: #374151;
        font-weight: 500;
      }

      span {
        font-size: 12px;
        color: #9ca3af;
      }
    }

    .file-list {
      .file-item {
        margin-bottom: 12px;
        padding: 12px;
        background: #f8fafc;
        border-radius: 6px;
        border: 1px solid #e5e7eb;

        &:last-child {
          margin-bottom: 0;
        }

        .file-info {
          display: flex;
          align-items: center;
          margin-bottom: 8px;

          i {
            font-size: 16px;
            color: #409EFF;
            margin-right: 8px;
          }

          .file-details {
            flex: 1;

            .file-name {
              display: block;
              font-size: 14px;
              color: #1f2937;
              font-weight: 500;
              margin-bottom: 2px;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }

            .file-size {
              font-size: 12px;
              color: #6b7280;
            }
          }

          .file-status {
            margin-right: 8px;

            .uploading {
              color: #409EFF;
              font-size: 12px;

              i {
                animation: rotating 2s linear infinite;
              }
            }

            .success {
              color: #67C23A;
              font-size: 12px;
            }

            .error {
              color: #f56c6c;
              font-size: 12px;
            }
          }

          .remove-btn {
            color: #ef4444;
            padding: 4px;

            &:hover {
              color: #f87171;
              background-color: #fef2f2;
              border-radius: 4px;
            }
          }
        }

        .progress-bar {
          margin-top: 8px;
        }

        .error-message {
          margin-top: 8px;
          font-size: 12px;
          color: #f56c6c;
          background-color: #fef2f2;
          padding: 4px 8px;
          border-radius: 4px;
        }
      }
    }
  }

  .upload-actions {
    margin-top: 16px;
    text-align: center;

    .el-button {
      margin: 0 8px;
    }
  }

  .error-notice {
    margin-top: 12px;
    padding: 8px 12px;
    background-color: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 6px;
    color: #dc2626;
    font-size: 14px;
    display: flex;
    align-items: center;

    i {
      margin-right: 6px;
      font-size: 16px;
    }
  }
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style> 